import * as THREE from 'three';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import config from '../crucibleScene/config.json';
import { SceneConfig } from './config';

export default class CrucibleLoader {
  public loadingManager: THREE.LoadingManager;
  public gltfLoader: GLTFLoader;
  public textureLoader: THREE.TextureLoader;
  public ktx2Loader: KTX2Loader;
  public quality: { [key: string]: string };

  constructor(renderer: THREE.WebGLRenderer, textureQuality: string) {
    this.loadingManager = new THREE.LoadingManager();
    // @ts-ignore
    this.quality = config.sceneConfig.quality[textureQuality];

    // GLTF
    this.gltfLoader = new GLTFLoader(this.loadingManager);

    // decoder + transcoder paths are relative to public folder and have to start with /
    this.gltfLoader.setDRACOLoader(
      new DRACOLoader().setDecoderPath('/nft-assets/wasm/')
    );

    this.gltfLoader.setKTX2Loader(
      new KTX2Loader()
        .setTranscoderPath('/nft-assets/wasm/')
        .detectSupport(renderer)
    );

    // Ended up using Draco compression instead of Meshopt, but leaving this here for now
    // this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);

    // Texture Loader
    this.textureLoader = new THREE.TextureLoader();

    // KTX2 Texture Loader
    this.ktx2Loader = new KTX2Loader()
      .setTranscoderPath('/nft-assets/wasm/')
      .detectSupport(renderer);
  }

  changeTextureQuality(textureQuality: string) {
    // @ts-ignore
    this.quality = config.sceneConfig.quality[textureQuality];
  }

  async loadModel(modelUrl: string) {
    try {
      const sceneConfig: SceneConfig = config.sceneConfig;
      if (
        this.quality.key !== 'medium' &&
        modelUrl.includes(sceneConfig.modelsBaseFolder)
      ) {
        const modelsQualityFolder = this.quality.modelsFolder;

        modelUrl = modelUrl.replace(
          sceneConfig.quality['medium'].modelsFolder,
          modelsQualityFolder
        );
      }

      const gltf: GLTF = await this.gltfLoader.loadAsync(modelUrl);
      return gltf;
    } catch (error) {
      console.log('Error Loading', modelUrl);
      throw Error;
    }
  }

  async loadTexture(
    textureUrl: string,
    transparency: boolean = false,
    ignoreQualitySettings = false
  ) {
    try {
      let textureFormat = this.quality.textureFormat;
      if (transparency) {
        textureFormat = this.quality.transparencyFormat;
      }
      const sceneConfig: SceneConfig = config.sceneConfig;

      if (
        this.quality.key !== 'medium' &&
        textureUrl.includes(sceneConfig.modelsBaseFolder)
      ) {
        const modelsQualityFolder = this.quality.modelsFolder;
        textureUrl = textureUrl.replace(
          sceneConfig.quality['medium'].modelsFolder,
          modelsQualityFolder
        );
      }

      if (textureFormat === 'ktx2' && !ignoreQualitySettings) {
        const texture: THREE.Texture = await this.ktx2Loader.loadAsync(
          textureUrl
        );
        return texture;
      } else {
        const texture: THREE.Texture = await this.textureLoader.loadAsync(
          textureUrl
        );
        return texture;
      }
    } catch (error) {
      console.log('Error loading', textureUrl);
      throw Error;
    }
  }

  loadSprite(spriteUrl: string) {
    return this.textureLoader.load(spriteUrl);
  }
}
